import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { lazyPage } from '@/lib/lazyPage';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { queryClient } from '@/lib/queryClient';
import { useSocketQueryBridge } from '@/hooks/useSocketQueryBridge';

// React Query devtools — DEV-only floating inspector. Lazy + tree-shaken
// out of the prod bundle entirely because the import is gated behind
// `import.meta.env.DEV` and Vite hoists the constant before rollup runs.
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : null;

// Pages
// Login pages stay eager — they are the entry chunk for unauthenticated users
// and their strings live in the bootstrap `auth` chunk loaded by i18n.ts.
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';

// Authenticated pages: each route is code-split via lazyPage(), which fetches
// the page JS chunk AND its i18n chunks in parallel. The second argument must
// match chunk names in scripts/split-locales.mjs KEY_TO_CHUNK.
// The Suspense fallback lives inside MainLayout around <Outlet />.
const Dashboard       = lazyPage(() => import('./pages/Dashboard'),       ['dashboard']);
const Dispatcher      = lazyPage(() => import('./pages/Dispatcher'),      ['dispatcher']);
// Tours Details loads the permanentTours chunk too — it reuses the run
// status/direction labels from `permanentTours.runs.*`.
const ToursDetails    = lazyPage(() => import('./pages/ToursDetails'),     ['toursDetails', 'permanentTours']);
const FleetManagement = lazyPage(() => import('./pages/FleetManagement'), ['fleetDb']);
const Users           = lazyPage(() => import('./pages/Users'),           ['users']);
// Drivers reuses the Users table + dialog, so it loads the same `users` chunk.
const Drivers         = lazyPage(() => import('./pages/Drivers'),         ['users']);
const Institutions    = lazyPage(() => import('./pages/Institutions'),    ['institutions']);
// Settings surfaces key/value editors that share labels with Orders (see 'orders.*' keys in Settings.tsx).
const Settings        = lazyPage(() => import('./pages/Settings'),        ['settings', 'orders']);
const Plugins         = lazyPage(() => import('./pages/Plugins'),         ['plugins']);
// Devices reuses Users' OrgPicker, which reads from the `users` locale chunk.
const Devices         = lazyPage(() => import('./pages/Devices'),         ['devices', 'users']);
const Rules           = lazyPage(() => import('./pages/Rules'),           ['rules']);
// Widgets (admin) reuses the `dashboard` chunk (dashboard widget titles + admin copy)
// and `reports` (report-widget titles, shown on the Reports tab).
const Widgets         = lazyPage(() => import('./pages/Widgets'),         ['dashboard', 'reports']);
// Profile reuses `settings` keys for session-timeout copy.
const Profile         = lazyPage(() => import('./pages/Profile'),         ['profile', 'settings']);
const AuditLog        = lazyPage(() => import('./pages/AuditLog'),        ['audit']);
const PermanentTours = lazyPage(() => import('./pages/PermanentTours'), ['permanentTours']);
// Recurring Trips — single-patient recurring order templates.
const RecurringTrips  = lazyPage(() => import('./pages/RecurringTrips'),  ['recurringTrips']);
const TourPlanning    = lazyPage(() => import('./pages/TourPlanning'),    ['tourPlanning']);
// Orders page imports Fleet components, so it needs the fleetDb chunk too.
const Orders          = lazyPage(() => import('./pages/Orders'),          ['orders', 'fleetDb']);
// Scheduler chunk contains `fleet` + `scheduler` + `orderDialog` (OrderDialog, CancelRideDialog, DeleteRideDialog).
// Scheduler subcomponents also use `orders` (OrdersStrip) and `institutions` (AddressAutocomplete inside OrderDialog).
const Scheduler       = lazyPage(() => import('./pages/Scheduler'),       ['scheduler', 'orders', 'institutions']);
// Reports (analytics board) reuses the `dashboard` chunk for the shared widget
// shell / board strings (loadError, retry, empty, remove) alongside `reports`.
const Reports         = lazyPage(() => import('./pages/Reports'),         ['reports', 'dashboard']);
// Resources — staff-only page listing downloadable files served by the API.
const Resources       = lazyPage(() => import('./pages/Resources'),       ['resources']);
// Documents hub — its tabs list drivers, vehicles, orders and customers, so it
// reuses those pages' chunks for the status labels shown on each owner row.
const Documents       = lazyPage(() => import('./pages/Documents'),       ['documents']);

// Layout
import MainLayout from './components/Layout/MainLayout';
import AccountLayout from './components/Layout/AccountLayout';

// Auth
import { useAuthStore, userHasAnyRole, userHasRole } from './store/authStore';
import { USER_ROLES, type UserRole } from '@kabbi/shared/constants';
import { useThemeStore, applyTheme, type Theme } from './store/themeStore';
import { useSettingsStore } from './store/settingsStore';
import { useIdleLogout } from './hooks/useIdleLogout';
import { IdleWarningDialog } from './components/IdleWarningDialog';
import { usePreferencesStore } from './store/preferencesStore';
import i18n from './i18n';

const STAFF_ROLES: UserRole[] = [USER_ROLES.ADMIN, USER_ROLES.DISPATCHER];

function AppContent() {
  const { isAuthenticated, user, checkAuth, refresh } = useAuthStore();
  const { theme } = useThemeStore();
  const { fetchSettings, getNumber } = useSettingsStore();
  const { fetch: fetchPrefs } = usePreferencesStore();
  const [ready, setReady] = useState(false);

  useEffect(() => { applyTheme(theme); }, [theme]);
  // Fetch settings ONCE after the user is authenticated. The store persists
  // them to localStorage, so subsequent page loads reuse the cached values
  // without a network call. Settings rarely change at runtime — admin
  // updates flow through the optimistic `updateSetting` action instead, so
  // we don't need focus/visibility refetch loops.
  useEffect(() => {
    if (isAuthenticated) fetchSettings();
  }, [isAuthenticated, fetchSettings]);

  // Proactively refresh the access token every 12 min (expires in 15 min by default)
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => { refresh().catch(() => {}); }, 12 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, refresh]);

  const idleTimeoutMin = getNumber('session_idle_timeout_min', 30);
  const { warningVisible, secondsLeft, continueSession, doLogout } = useIdleLogout(idleTimeoutMin);

  // Bridge socket events → TanStack Query invalidations. Re-attaches when
  // auth flips so post-login `reconnectSocket()`'s listener wipe doesn't
  // leave the bridge dangling on a dead socket.
  useSocketQueryBridge(isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) { setReady(true); return; }
    checkAuth().then(() => fetchPrefs()).then(() => {
      const { getPref } = usePreferencesStore.getState();
      // Only override the persisted themeStore when the server actually has a
      // saved theme. Otherwise the unconditional fallback would clobber the
      // freshly-rehydrated localStorage value with a hard-coded default —
      // visible as "set theme on Profile → F5 → reverted to default" if the
      // server hasn't received the PATCH yet (no-await on setPref racing F5).
      const savedTheme = getPref<string>('theme');
      if (savedTheme) useThemeStore.getState().setTheme(savedTheme as Theme);
      const savedLocale = getPref<string>('locale');
      if (savedLocale) { i18n.changeLanguage(savedLocale); localStorage.setItem('kabbi_lang', savedLocale); }
    }).finally(() => setReady(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ready) return null;

  const isStaff = userHasAnyRole(user, STAFF_ROLES);

  return (
    <TooltipProvider>
      <Toaster richColors position="top-right" />
      {ReactQueryDevtools && (
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
        </Suspense>
      )}
      <IdleWarningDialog
        open={warningVisible}
        secondsLeft={secondsLeft}
        onContinue={continueSession}
        onLogout={doLogout}
      />
      <BrowserRouter>
        {/* Root error boundary — a render error in any lazy page lands
            on the localized fallback below instead of blanking the entire
            admin to a white screen. Per-route boundaries can be added on
            top of this where the inner component is especially crash-
            prone (Bryntum, Mapbox GL). */}
        <ErrorBoundary tag="root">
        <Routes>
          <Route path="/login"        element={isAuthenticated ? <Navigate to={isStaff ? '/dashboard' : '/profile'} /> : <Login />} />
          <Route path="/register"     element={isAuthenticated ? <Navigate to={isStaff ? '/dashboard' : '/profile'} /> : <Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Staff layout — full admin panel */}
          <Route
            path="/"
            element={isAuthenticated && isStaff ? <MainLayout /> : isAuthenticated ? <Navigate to="/profile" /> : <Navigate to="/login" />}
          >
            <Route index element={<Navigate to="/dashboard" />} />
            <Route path="dashboard"  element={<Dashboard />} />
            <Route path="dispatcher" element={<Dispatcher />} />
            <Route path="tours-details" element={<ToursDetails />} />
            {/* Back-compat: the page was renamed from /rides. */}
            <Route path="rides" element={<Navigate to="/tours-details" replace />} />
            <Route path="orders" element={<Orders />} />
            <Route path="fleet" element={<Navigate to="/scheduler" />} />
            <Route path="scheduler" element={<Scheduler />} />
            <Route path="fleet-db" element={userHasAnyRole(user, [USER_ROLES.ADMIN, USER_ROLES.DISPATCHER]) ? <FleetManagement /> : <Navigate to="/dashboard" />} />
            <Route path="users" element={<Users />} />
            <Route path="drivers" element={<Drivers />} />
            <Route path="institutions" element={userHasRole(user, USER_ROLES.ADMIN) ? <Institutions /> : <Navigate to="/dashboard" />} />
            <Route path="settings" element={<Settings />} />
            <Route path="devices" element={userHasRole(user, USER_ROLES.ADMIN) ? <Devices /> : <Navigate to="/dashboard" />} />
            <Route path="plugins" element={<Plugins />} />
            <Route path="rules" element={userHasRole(user, USER_ROLES.ADMIN) ? <Rules /> : <Navigate to="/dashboard" />} />
            <Route path="widgets" element={userHasRole(user, USER_ROLES.ADMIN) ? <Widgets /> : <Navigate to="/dashboard" />} />
            <Route path="profile" element={<Profile />} />
            <Route path="audit" element={userHasRole(user, USER_ROLES.ADMIN) ? <AuditLog /> : <Navigate to="/dashboard" />} />
            <Route
              path="reports"
              element={userHasAnyRole(user, [USER_ROLES.ADMIN, USER_ROLES.DISPATCHER]) ? <Reports /> : <Navigate to="/dashboard" />}
            />
            <Route
              path="documents"
              element={userHasAnyRole(user, [USER_ROLES.ADMIN, USER_ROLES.DISPATCHER]) ? <Documents /> : <Navigate to="/dashboard" />}
            />
            <Route
              path="resources"
              element={userHasAnyRole(user, [USER_ROLES.ADMIN, USER_ROLES.DISPATCHER]) ? <Resources /> : <Navigate to="/dashboard" />}
            />
            <Route
              path="permanent-tours"
              element={userHasAnyRole(user, [USER_ROLES.ADMIN, USER_ROLES.DISPATCHER]) ? <PermanentTours /> : <Navigate to="/dashboard" />}
            />
            <Route
              path="recurring-trips"
              element={userHasAnyRole(user, [USER_ROLES.ADMIN, USER_ROLES.DISPATCHER]) ? <RecurringTrips /> : <Navigate to="/dashboard" />}
            />
            <Route
              path="tour-planning"
              element={userHasAnyRole(user, [USER_ROLES.ADMIN, USER_ROLES.DISPATCHER]) ? <TourPlanning /> : <Navigate to="/dashboard" />}
            />
          </Route>

          {/* Account-only layout — drivers and passengers (staff use the nested /profile inside MainLayout) */}
          {!isStaff && (
            <Route
              path="/profile"
              element={isAuthenticated ? <AccountLayout /> : <Navigate to="/login" />}
            >
              <Route index element={<Profile />} />
            </Route>
          )}
        </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  );
}

/**
 * Top-level app wrapper. Mounts every cross-cutting provider that needs
 * to live above every route + hook in the admin tree:
 *   - `QueryClientProvider` so `useQuery` / `useMutation` work anywhere.
 * Inner providers (TooltipProvider, ErrorBoundary, BrowserRouter) sit
 * inside `AppContent` so they can read auth + theme state.
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}

export default App;
